import { useCallback, useState, useEffect } from 'react';

interface UseViewportScrollOptions {
  headerHeight?: number;
  footerHeight?: number;
  overlapLines?: number;
  fontSize: number;
  lineHeight: number;
}

interface UseViewportScrollResult {
  scrollToNextPage: () => boolean;
  scrollToPrevPage: () => boolean;
  scrollProgress: number;
  isAtTop: boolean;
  isAtBottom: boolean;
}

export function useViewportScroll({
  headerHeight = 56,
  footerHeight = 48,
  overlapLines = 2,
  fontSize,
  lineHeight,
}: UseViewportScrollOptions): UseViewportScrollResult {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const getScrollDistance = useCallback(() => {
    const viewportHeight = window.innerHeight;
    const overlapPx = overlapLines * fontSize * lineHeight;
    return viewportHeight - headerHeight - footerHeight - overlapPx;
  }, [headerHeight, footerHeight, overlapLines, fontSize, lineHeight]);

  const scrollToNextPage = useCallback(() => {
    const distance = getScrollDistance();
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const currentScroll = window.scrollY;

    if (currentScroll >= maxScroll - 10) {
      return false; // 已到底部
    }

    window.scrollBy({ top: distance, behavior: 'smooth' });
    return true;
  }, [getScrollDistance]);

  const scrollToPrevPage = useCallback(() => {
    const distance = getScrollDistance();

    if (window.scrollY <= 10) {
      return false; // 已到頂部
    }

    window.scrollBy({ top: -distance, behavior: 'smooth' });
    return true;
  }, [getScrollDistance]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;

      setScrollProgress(scrollHeight > 0 ? scrollTop / scrollHeight : 0);
      setIsAtTop(scrollTop <= 10);
      setIsAtBottom(scrollTop >= scrollHeight - 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return {
    scrollToNextPage,
    scrollToPrevPage,
    scrollProgress,
    isAtTop,
    isAtBottom,
  };
}
